// What's new (the red dot and the panel). The entries ship inside the build from src/whats-new.md; the newest
// entry's id is what a person has or has not seen, remembered per user in localStorage like the drawer state.
import { useSyncExternalStore } from 'react';
import raw from '../whats-new.md?raw';

export interface WhatsNewEntry {
  /** "YYYY-MM-DD Title": stable once shipped. */
  id: string;
  date: string;
  title: string;
  changes: string[];
}

/** Level-two headings "## YYYY-MM-DD Title" open entries; "- " lines under them are the changes. Anything else is ignored. */
export function parseWhatsNew(markdown: string): WhatsNewEntry[] {
  const entries: WhatsNewEntry[] = [];
  let current: WhatsNewEntry | undefined;
  for (const line of markdown.split(/\r?\n/)) {
    const heading = /^##\s+(\d{4}-\d{2}-\d{2})\s+(.+?)\s*$/.exec(line);
    if (heading) {
      current = { id: `${heading[1]} ${heading[2]}`, date: heading[1]!, title: heading[2]!, changes: [] };
      entries.push(current);
      continue;
    }
    const bullet = /^\s*[-*]\s+(.+?)\s*$/.exec(line);
    if (bullet && current) current.changes.push(bullet[1]!);
  }
  return entries;
}

export const entries: WhatsNewEntry[] = parseWhatsNew(raw);

const EVENT = 'jabberr-whats-new-read';
const memory = new Map<string, string>();
const keyFor = (userId: string) => `jabberr.whats-new-read:${encodeURIComponent(userId)}`;

function readSeen(userId: string): string | null {
  const key = keyFor(userId);
  try {
    return memory.get(key) ?? localStorage.getItem(key);
  } catch {
    return memory.get(key) ?? null;
  }
}

function subscribe(callback: () => void): () => void {
  window.addEventListener(EVENT, callback);
  window.addEventListener('storage', callback);
  return () => {
    window.removeEventListener(EVENT, callback);
    window.removeEventListener('storage', callback);
  };
}

/** True until this person has opened the panel since the newest entry shipped. */
export function useUnreadWhatsNew(userId: string): boolean {
  const seen = useSyncExternalStore(subscribe, () => readSeen(userId), () => null);
  const latest = entries[0];
  return Boolean(userId && latest && seen !== latest.id);
}

export function markWhatsNewRead(userId: string): void {
  const latest = entries[0];
  if (!userId || !latest) return;
  const key = keyFor(userId);
  try {
    localStorage.setItem(key, latest.id);
    memory.delete(key);
  } catch {
    // Storage blocked: keep the dot off for this session at least.
    memory.set(key, latest.id);
  }
  window.dispatchEvent(new Event(EVENT));
}
